import { Store } from '@ngrx/store';
import { AppInjector } from '../app.injector';
import { ArmyBuilderConfig } from '../config';
import { HttpRequestConfig, RestDataService } from '../restDataService/restDataService';
import { NamedEntity, mapToPaginatedItems } from '../operators';
import { snapshot } from '../utils';

import { catchError, Observable, of, shareReplay, throwError } from 'rxjs';
import { ENTITY_LIBRARY_ACTIONS } from '../army-builder-entity-store';
import { NativeStorageService } from '../data/native-storage.service';

/**
 * Bump this when cached entity data has to be thrown away rather than topped up.
 *
 * `RestDataService` refreshes a library with `?since=<newest lastUpdated in the cache>`, so the
 * server only ever returns entities it has touched since. That can never repair data the *client*
 * corrupted: the bad entities still carry their original `lastUpdated`, and the item count still
 * matches, so neither the delta fetch nor the stale-cache check displaces them. Changing the
 * version changes the cache key, the lookup misses, and the next fetch is a full download — which
 * is the only lever that heals an already-poisoned install.
 *
 * v2: k47's unit-template merge rewrote bolt-action's FAST rule to FAST_VEHICLE in place, so the
 *     `bolt-action-3e_unit-templates` cache of anyone who ever opened k47 holds k47-only rule ids.
 */
export const ENTITY_CACHE_VERSION = 2;

const entityCacheKey = (gameId: string, dataType: string) => `${gameId}_${dataType}_v${ENTITY_CACHE_VERSION}`;

// Keys written by earlier versions are dead weight once the new one is populated — on mobile that
// is megabytes of unit templates per game. Purged only after a successful fetch, so a failed
// refresh never leaves the device with neither copy, and only once per key per session.
const purgedCacheKeys = new Set<string>();
const purgeSupersededCaches = (gameId: string, dataType: string) => {
    const superseded = [`${gameId}_${dataType}`];
    for (let version = 1; version < ENTITY_CACHE_VERSION; version++) {
        superseded.push(`${gameId}_${dataType}_v${version}`);
    }

    superseded
        .filter((key) => !purgedCacheKeys.has(key))
        .forEach((key) => {
            purgedCacheKeys.add(key);
            AppInjector.get(NativeStorageService)
                .removeItem(key)
                .catch(() => {});
        });
};

export const getDataFromApi = <T>(
    gameId: string,
    dataType: string,
    opts?: HttpRequestConfig & { ignoreLibraryUpdates?: boolean },
    callback?: ({ data }: { data: T[] }) => void
) => {
    const store = AppInjector.get(Store);
    const restDataService = AppInjector.get(RestDataService);
    const config = AppInjector.get(ArmyBuilderConfig);

    if (!opts?.ignoreLibraryUpdates) {
        store.dispatch(ENTITY_LIBRARY_ACTIONS.UPDATE_LIBRARY_STATUS({ gameId, dataType, status: 'LOADING' }));
    }
    let req = restDataService
        .get(config.apiBaseUrl + '/library/' + gameId + '/' + dataType + '?getAll=1', {
            cacheKey: entityCacheKey(gameId, dataType),
            requiresLogin: true,
            mergeWithCache: true,
            ...(opts ?? {})
        })
        .pipe(
            catchError((err) => {
                if (!opts?.ignoreLibraryUpdates) {
                    store.dispatch(ENTITY_LIBRARY_ACTIONS.UPDATE_LIBRARY_STATUS({ gameId, dataType: dataType as string, status: 'ERROR' }));
                }
                console.error(err);
                throw throwError(() => err);
            }),
            mapToPaginatedItems<T>(false),
            catchError(() => of(null)),
            shareReplay(1)
        );

    req.subscribe((data) => {
        if (!data) {
            return;
        }
        purgeSupersededCaches(gameId, dataType);
        if (callback) {
            callback({ data });
        } else if (!opts?.ignoreLibraryUpdates) {
            store.dispatch(ENTITY_LIBRARY_ACTIONS.LOAD_ENTITY_DATA_AVAILABLE({ gameId, dataType: dataType as string, data }));
        }
    });
    return req;
};

export const resetData = (arr: any[], data: any[]) => {
    // If we don't clear the array first we'll end up with duplicates (special rules spam)
    arr.length = 0;
    arr.push(...data);
};

export const staticData = <T extends NamedEntity>(
    o: Observable<T[]>,
    // type: string,
    dataArray: T[] | { [key: string]: T }
    // cacheKey?: string
) => {
    // if (!cacheKey) {
    //     cacheKey = capitalise(type);
    // }

    snapshot(o, (data) => {
        if (Array.isArray(dataArray)) {
            // If we don't clear the array first we'll end up with duplicates (special rules spam)
            dataArray.length = 0;
            dataArray.push(...data);
        } else {
            data.forEach((item) => {
                if (item.key?.includes(' ')) {
                    // console.error(item.key);
                }
                dataArray[item.key] = item;
            });
        }
        // deepFreeze(dataArray);
    });
};
